import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { paths } from '../app/paths';
import { useCountdown } from '../app/useCountdown';
import { isIos, isStandalone, usePwa } from '../app/usePwa';
import { usePush } from '../app/usePush';
import { useDismissed } from '../app/useDismissed';
import { prepareAvatar } from '../app/prepareImage';
import {
  useHome,
  useLogout,
  useRemoveAvatar,
  useSession,
  useStandings,
  useUpdateProfile,
  useUploadAvatar,
  useWeekList,
  useWeekResults,
} from '../api/queries';
import { asyncState, combineAsync, messageFor } from './asyncState';
import * as screens from '../ui/screens/player';

/** Home dashboard — the "what do I need to do right now" screen. */
export function HomeRoute() {
  const session = useSession();
  const home = useHome();
  const countdown = useCountdown(home.data?.week?.nextLockAt ?? null);
  const isCommissioner = session.data?.activeRole === 'commissioner';

  const push = usePush(Boolean(session.data));
  const pwa = usePwa();
  const installDismissal = useDismissed('install', 30);
  const notifyDismissal = useDismissed('notifications', 30);

  // Only offered when the browser will actually do something with it: either it hands us a
  // native prompt, or it is an iPhone in a tab, where the Share-sheet route genuinely
  // exists and is the only one. Desktop Firefox and Safari get nothing, because there is
  // nothing accurate to tell them.
  const canInstall = !isStandalone() && (pwa.promptInstall !== null || isIos());
  const showInstall = canInstall && !installDismissal.isDismissed;

  // Deliberately second in line. On iOS the browser will refuse notification permission to
  // a page in a tab, so asking before the app is installed is asking for a refusal that
  // cannot be walked back.
  const canAskForNotifications =
    push.configured &&
    !push.subscribed &&
    push.permission !== 'denied' &&
    push.support === 'supported';
  const showNudge = !showInstall && canAskForNotifications && !notifyDismissal.isDismissed;

  return (
    <screens.Home
      {...asyncState(home)}
      home={home.data ?? null}
      countdown={countdown}
      picksHref={paths.picks}
      standingsHref={paths.standings}
      resultsHref={paths.results}
      historyHref={paths.history}
      commishHref={isCommissioner ? paths.commish : null}
      isCommissioner={isCommissioner}
      installPrompt={
        showInstall
          ? {
              onInstall: pwa.promptInstall,
              isIos: isIos(),
              // The stakes are higher on iOS: installing is not a nicety there, it is the
              // precondition for ever receiving a notification at all.
              mentionNotifications: isIos() && push.support === 'needs-install',
              onDismiss: installDismissal.dismiss,
            }
          : null
      }
      notificationNudge={
        showNudge ? { settingsHref: paths.settings, onDismiss: notifyDismissal.dismiss } : null
      }
    />
  );
}

export function StandingsRoute() {
  const session = useSession();
  const standings = useStandings(session.data?.activeSeason?.id);

  return <screens.Standings {...asyncState(standings)} standings={standings.data ?? null} />;
}

/** Weekly results grid, with a week switcher. */
export function ResultsRoute() {
  const session = useSession();
  const seasonId = session.data?.activeSeason?.id ?? null;
  const weekList = useWeekList(seasonId);

  const [selectedWeekId, setSelectedWeekId] = useState<string | null>(null);
  const currentWeekId = session.data?.currentWeek?.id ?? null;
  const weekId = selectedWeekId ?? currentWeekId;

  const results = useWeekResults(weekId);

  return (
    <screens.WeekResults
      {...combineAsync(results, weekList)}
      results={results.data ?? null}
      weeks={weekList.data ?? []}
      selectedWeekId={weekId}
      onSelectWeek={setSelectedWeekId}
    />
  );
}

export function HistoryRoute() {
  const navigate = useNavigate();
  const session = useSession();
  const weekList = useWeekList(session.data?.activeSeason?.id);

  return (
    <screens.History
      {...asyncState(weekList)}
      weeks={weekList.data ?? []}
      seasonName={session.data?.activeSeason?.name ?? null}
      onOpenWeek={(weekId) => navigate(paths.picksForWeek(weekId))}
    />
  );
}

export function SettingsRoute() {
  const navigate = useNavigate();
  const session = useSession();
  const updateProfile = useUpdateProfile();
  const logout = useLogout();
  const pwa = usePwa();
  const push = usePush(Boolean(session.data));
  const uploadAvatar = useUploadAvatar();
  const removeAvatar = useRemoveAvatar();
  const [notice, setNotice] = useState<string | null>(null);

  // Local preview of a picture that is still uploading, so the new face appears the moment
  // it is chosen rather than after the round trip. Held here rather than in the screen
  // because the resize happens here — the screen only ever receives a URL.
  const [pendingAvatar, setPendingAvatar] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  // Clear the confirmation once the user moves on, so it does not linger as stale praise.
  useEffect(() => {
    if (!notice) return;
    const handle = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(handle);
  }, [notice]);

  // Devices are fetched here rather than with the push status, because this is the only
  // screen that shows them — the home badge uses the same hook and has no use for a list.
  const { loadDevices } = push;
  useEffect(() => {
    if (session.data) loadDevices();
  }, [session.data, loadDevices]);

  const chooseAvatar = (file: File) => {
    setAvatarError(null);
    prepareAvatar(file).then(
      (prepared) => {
        setPendingAvatar(prepared.previewUrl);
        uploadAvatar.mutate(
          { mimeType: prepared.mimeType, data: prepared.data },
          {
            // The preview is cleared on success too: by then the session has the real URL,
            // and holding a data: URI of the same image would just pin it in memory.
            onSuccess: () => {
              setPendingAvatar(null);
              setNotice('Picture updated');
            },
            onError: (error: unknown) => {
              setPendingAvatar(null);
              setAvatarError(messageFor(error));
            },
          },
        );
      },
      (error: unknown) => setAvatarError(messageFor(error)),
    );
  };

  return (
    <screens.Settings
      session={session.data ?? null}
      avatar={{
        url: session.data?.user.avatarUrl ?? null,
        pendingUrl: pendingAvatar,
        isSaving: uploadAvatar.isPending || removeAvatar.isPending,
        error: avatarError,
        onChoose: chooseAvatar,
        onRemove: () => {
          setAvatarError(null);
          removeAvatar.mutate(undefined, {
            onError: (error: unknown) => setAvatarError(messageFor(error)),
          });
        },
      }}
      notifications={{
        support: push.support,
        configured: push.configured,
        permission: push.permission,
        subscribed: push.subscribed,
        deviceCount: push.deviceCount,
        isBusy: push.isBusy,
        error: push.error,
        notice: push.notice,
        reminderHours: push.reminderHours,
        finalCallHours: push.finalCallHours,
        onEnable: push.enable,
        onDisable: push.disable,
        onSendTest: push.sendTest,
        devices: push.devices,
        devicesLoaded: push.devicesLoaded,
        removingDeviceId: push.removingDeviceId,
        onRemoveDevice: push.removeDevice,
      }}
      isSaving={updateProfile.isPending}
      error={updateProfile.error ? messageFor(updateProfile.error) : null}
      notice={notice}
      onSaveProfile={(values) => {
        updateProfile.mutate(values, { onSuccess: () => setNotice('Saved') });
      }}
      onLogout={() => {
        logout.mutate(undefined, { onSuccess: () => navigate(paths.login) });
      }}
      onInstall={pwa.promptInstall}
      showIosInstallHint={pwa.showIosInstallHint}
    />
  );
}
